import { getBackendErrorMessage } from "@/api/errors";
import { AlertMessage } from "@/components/AlertMessage";
import Button from "@/components/bricks/button/Button";
import PlusIcon from "@/components/icons/PlusIcon";
import Modal, {
  ModalContext,
  ModalProvider,
  ModalTrigger,
} from "@/components/Modal/Modal";
import Spinner from "@/components/spinner/Spinner";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import { UserQueryContext } from "@/context/UserContext";
import { useKodeListe } from "@/hooks/useQueries";
import { useUpdateOverfoerteLektioner } from "@/hooks/person-hooks/useOverfoerteLektioner";
import { formatLessons } from "@/util/number";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext, useMemo } from "react";
import * as Yup from "yup";

import type { DecoratedPersonEfteruddannelse } from "./decoratePeriod";
import {
  buildOverfoerteLektionerFormValues,
  erOverfoerbar,
  formValuesToOverfoerteLinjer,
  getInjunctionOverflowByType,
  getOverfoerteLektionerDelta,
  getOverfoertIAltError,
  getTransferCapWarnings,
  getVisibleTransferTypes,
  getSaerligLektionerByType,
  kanRegistrereOverfoerteLektioner,
  MAX_OVERFOERT_I_ALT,
  MAX_OVERFOERT_PER_TYPE,
  type OverfoerteLektionerFormValues,
} from "./utils/overfoerteLektionerForm";

const SAVE_ERROR_FALLBACK = "Kunne ikke gemme overførte lektioner.";

const formatAendring = (value: number) =>
  `${value > 0 ? "+" : ""}${formatLessons(value)}`;

const validationSchema = Yup.object({
  lektionerPerType: Yup.lazy((value) =>
    Yup.object(
      Object.fromEntries(
        Object.keys(value ?? {}).map((key) => [
          key,
          Yup.number()
            .transform((v, original) => (original === "" ? 0 : v))
            .typeError("Skal være et tal")
            .min(0, "Kan ikke være negativ"),
        ]),
      ),
    ),
  ),
});

interface EditOverfoerteLektionerFormProps {
  personId: string;
  period: DecoratedPersonEfteruddannelse;
  /** Indberettede lektioner i den FORRIGE periode, til 54-lektionsadvarslen. */
  lektionerIndberettetForrigePeriode?: number | null;
  onSaved: () => void;
}

const EditOverfoerteLektionerForm = ({
  personId,
  period,
  lektionerIndberettetForrigePeriode,
  onSaved,
}: EditOverfoerteLektionerFormProps) => {
  const { setModalOpen } = useContext(ModalContext);
  const { kodeliste: efteruddannelsestyper } = useKodeListe(
    "Efteruddannelsestype",
  );
  const {
    mutate: gemOverfoerteLektioner,
    isPending,
    isError,
    error,
    data: resultat,
    reset: resetMutation,
  } = useUpdateOverfoerteLektioner();

  const typeLabels = useMemo(() => {
    const labels = new Map<number, string>();
    efteruddannelsestyper?.forEach((type) => {
      if (type.navn?.trim()) labels.set(type.nr, type.navn.trim());
    });
    return labels;
  }, [efteruddannelsestyper]);

  const initialValues = useMemo(
    () => buildOverfoerteLektionerFormValues(period),
    [period],
  );
  const injunctionOverflow = useMemo(
    () => getInjunctionOverflowByType(period),
    [period],
  );
  const injunctionOverflowTotal = useMemo(
    () => [...injunctionOverflow.values()].reduce((sum, v) => sum + v, 0),
    [injunctionOverflow],
  );

  const formik = useFormik<OverfoerteLektionerFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      // Spærringen håndhæves også af backenden; her forhindrer den blot et kald vi ved fejler.
      if (getOverfoertIAltError(values)) return;
      gemOverfoerteLektioner(
        {
          personId,
          efteruddannelseId: period.id,
          body: { linjer: formValuesToOverfoerteLinjer(values) },
        },
        {
          onSuccess: (svar) => {
            // Rettelsen er gemt i AIKA uanset hvad, så visningen skal opdateres.
            onSaved();
            if (svar.portalSynkroniseret) {
              resetMutation();
              setModalOpen(false);
            }
            // Gik portalen galt, holder vi modalen åben og viser beskeden, så sekretæren
            // kan trykke Gem igen. Kaldet er idempotent.
          },
        },
      );
    },
  });

  const visibleTypes = useMemo(
    () => getVisibleTransferTypes(getSaerligLektionerByType(period)),
    [period],
  );
  const delta = useMemo(
    () => getOverfoerteLektionerDelta(initialValues, formik.values),
    [initialValues, formik.values],
  );
  const iAltError = useMemo(
    () => getOverfoertIAltError(formik.values),
    [formik.values],
  );
  const warnings = useMemo(
    () =>
      getTransferCapWarnings(
        formik.values,
        typeLabels,
        lektionerIndberettetForrigePeriode,
      ),
    [formik.values, typeLabels, lektionerIndberettetForrigePeriode],
  );
  const warningByType = useMemo(() => {
    const perType = new Map<number, string>();
    warnings.forEach((w) => {
      if (w.typeNr != null && !perType.has(w.typeNr)) {
        perType.set(w.typeNr, w.message);
      }
    });
    return perType;
  }, [warnings]);

  const handleCancel = () => {
    formik.resetForm();
    resetMutation();
    setModalOpen(false);
  };

  const deltaForType = (typeNr: number) =>
    delta.rows.find((r) => r.typeNr === typeNr);

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <div className="c-modal-container">
          <div className="c-modal-container-inner">
            <AlertMessage type="info">
              <p className="mb-0">
                Felterne viser det{" "}
                <strong>samlede antal efter rettelsen</strong> — ikke antallet
                der lægges til.
              </p>
            </AlertMessage>

            <div className="table-responsive mt-3">
              <table className="table table-sm mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th className="text-end">Nu</th>
                    <th className="text-end">I alt efter rettelse</th>
                    <th className="text-end">Ændring</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTypes.map((typeNr) => {
                    const raekke = deltaForType(typeNr);
                    const loft = MAX_OVERFOERT_PER_TYPE[typeNr];
                    const advarsel = warningByType.get(typeNr);
                    const fieldName = `lektionerPerType.${typeNr}`;
                    return (
                      <tr key={typeNr}>
                        <td>
                          <label
                            className="form-label mb-0"
                            htmlFor={fieldName}
                          >
                            {typeLabels.get(typeNr) ?? `Type ${typeNr}`}
                          </label>
                          {erOverfoerbar(typeNr) ? (
                            <div className="text-muted small">højst {loft}</div>
                          ) : (
                            <div className="text-warning small">
                              ikke længere overførbar
                            </div>
                          )}
                        </td>
                        <td className="text-end text-muted">
                          {formatLessons(raekke?.foer ?? 0)}
                        </td>
                        <td className="text-end" style={{ maxWidth: "8rem" }}>
                          <input
                            autoComplete="off"
                            className={`form-control text-end${advarsel ? " border-warning" : ""}`}
                            id={fieldName}
                            min="0"
                            name={fieldName}
                            step="any"
                            type="number"
                            value={formik.values.lektionerPerType[typeNr] ?? ""}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                          />
                        </td>
                        <td className="text-end">
                          {raekke && raekke.aendring !== 0 ? (
                            <span
                              className={
                                raekke.aendring > 0
                                  ? "text-success fw-semibold"
                                  : "text-danger fw-semibold"
                              }
                            >
                              {formatAendring(raekke.aendring)}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="fw-semibold table-secondary">
                    <td>
                      Overført i alt
                      <div className="fw-normal text-muted small">
                        højst {MAX_OVERFOERT_I_ALT}
                      </div>
                    </td>
                    <td className="text-end">
                      {formatLessons(delta.totalFoer)}
                    </td>
                    <td
                      className={`text-end${iAltError ? " text-danger" : ""}`}
                    >
                      {formatLessons(delta.totalEfter)}
                    </td>
                    <td className="text-end">
                      {delta.totalAendring !== 0 ? (
                        <span
                          className={
                            delta.totalAendring > 0
                              ? "text-success"
                              : "text-danger"
                          }
                        >
                          {formatAendring(delta.totalAendring)}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {injunctionOverflowTotal > 0 && (
              <p className="text-muted small mt-3 mb-0">
                Overført via påbud/pålæg — redigeres ikke her:{" "}
                {formatLessons(injunctionOverflowTotal)}
              </p>
            )}

            {iAltError && (
              <div className="mt-3">
                <AlertMessage warningIcon type="danger">
                  <p className="mb-0">{iAltError}</p>
                </AlertMessage>
              </div>
            )}

            {warnings.length > 0 && (
              <div className="mt-3">
                <AlertMessage warningIcon type="warning">
                  <ul className="mb-0 ps-3">
                    {warnings.map((w) => (
                      <li key={`${w.typeNr ?? "total"}-${w.message}`}>
                        {w.message}
                      </li>
                    ))}
                  </ul>
                </AlertMessage>
              </div>
            )}

            {resultat && !resultat.portalSynkroniseret && (
              <div className="mt-3">
                <AlertMessage warningIcon type="warning">
                  <p className="mb-0">
                    {resultat.portalBesked ??
                      "Lektionerne er gemt i AIKA, men indberetningsportalen blev ikke opdateret. Tryk Gem igen."}
                  </p>
                </AlertMessage>
              </div>
            )}

            {isError && (
              <div className="mt-3">
                <AlertMessage warningIcon type="danger">
                  <p className="mb-0">
                    {getBackendErrorMessage(error, SAVE_ERROR_FALLBACK)}
                  </p>
                </AlertMessage>
              </div>
            )}
          </div>

          <StickyFooter className="border-top">
            <Button color="secondary" type="button" onClick={handleCancel}>
              Annuller
            </Button>
            <div className="d-flex gap-2 align-items-center">
              {isPending && <Spinner size={24} />}
              <Button
                color="primary"
                disabled={isPending || Boolean(iAltError)}
                type="submit"
              >
                Gem
              </Button>
            </div>
          </StickyFooter>
        </div>
      </Form>
    </FormikProvider>
  );
};

interface EditOverfoerteLektionerModalProps {
  personId: string;
  period: DecoratedPersonEfteruddannelse;
  /** Findes der en foregående periode? Uden en er der intet at overføre fra, og backenden svarer 400. */
  harForegaaendePeriode: boolean;
  lektionerIndberettetForrigePeriode?: number | null;
  onSaved: () => void;
}

export const EditOverfoerteLektionerModal = ({
  personId,
  period,
  harForegaaendePeriode,
  lektionerIndberettetForrigePeriode,
  onSaved,
}: EditOverfoerteLektionerModalProps) => {
  const { user, hasPermission } = useContext(UserQueryContext);

  // Kun perioder advokaten stadig kan handle på. Backenden håndhæver samme regel; her undgår vi at
  // tilbyde en handling, der ville blive afvist.
  if (
    !hasPermission(user, "TilsynWrite") ||
    !harForegaaendePeriode ||
    !kanRegistrereOverfoerteLektioner(period)
  ) {
    return null;
  }

  return (
    <ModalProvider>
      <ModalTrigger
        asChild
        className="btn btn-primary btn--transparent has-icon px-2 justify-content-end"
      >
        <button
          id={`efteruddannelse-tilfoej-overfoerte-lektioner-${period.id}`}
          type="button"
        >
          Tilføj overførte lektioner
          <span className="icon-bg badge">
            <PlusIcon />
          </span>
        </button>
      </ModalTrigger>
      <Modal title="Tilføj overførte lektioner">
        <EditOverfoerteLektionerForm
          personId={personId}
          period={period}
          lektionerIndberettetForrigePeriode={
            lektionerIndberettetForrigePeriode
          }
          onSaved={onSaved}
        />
      </Modal>
    </ModalProvider>
  );
};
